import { ActionIcon, Group, Paper, ScrollArea, Stack, Text } from '@mantine/core';
import { useState } from 'react';
import { IconPencil } from '@tabler/icons-react';
import { addDays, format } from 'date-fns';
import { primaryColor } from '../../constants/theme';
import { DatePickerModal } from './DatePickerModal';

export const getDefaultEndDate = () => format(addDays(new Date(), 7), 'dd MMM yyyy');

const DAYS = [
  { label: 'Mon', value: 1 },
  { label: 'Tue', value: 2 },
  { label: 'Wed', value: 3 },
  { label: 'Thu', value: 4 },
  { label: 'Fri', value: 5 },
  { label: 'Sat', value: 6 },
  { label: 'Sun', value: 7 },
];

interface RecurringDatesProps {
  selectedDays: Set<number>;
  onDaysChange: (days: Set<number>) => void;
  endDate: string;
  onEndDateChange: (date: string) => void;
}

export const RecurringDates = ({ selectedDays, onDaysChange, endDate, onEndDateChange }: RecurringDatesProps) => {
  const [showDatePicker, setShowDatePicker] = useState(false);

  const toggleDay = (day: number) => {
    const next = new Set(selectedDays);
    if (next.has(day)) {
      next.delete(day);
    } else {
      next.add(day);
    }
    onDaysChange(next);
  };

  return (
    <Stack gap="sm">
      <Paper withBorder shadow="none" radius="sm" p="sm">
        <Stack gap="sm">
          <Text size="sm" fw={600} ta="left">
            Repeat On
          </Text>
          <ScrollArea type="auto" offsetScrollbars>
            <Group wrap="nowrap" gap="xs" pb={4}>
              {DAYS.map(({ label, value }) => {
                const selected = selectedDays.has(value);
                return (
                  <ActionIcon
                    key={value}
                    radius="xl"
                    size={48}
                    variant={selected ? 'filled' : 'outline'}
                    color={primaryColor}
                    onClick={() => toggleDay(value)}
                    aria-label={label}
                    aria-pressed={selected}
                    style={{ flexShrink: 0 }}
                  >
                    <Text size="xs" fw={600} c={selected ? 'white' : primaryColor}>
                      {label}
                    </Text>
                  </ActionIcon>
                );
              })}
            </Group>
          </ScrollArea>
        </Stack>
      </Paper>

      <Paper
        withBorder
        shadow="none"
        radius="sm"
        p="sm"
        style={{ cursor: 'pointer' }}
        onClick={() => setShowDatePicker(true)}
      >
        <Group justify="space-between" align="center">
          <Group gap={6}>
            <Text>End Date:</Text>
            <Text c={primaryColor} fw={600}>
              {endDate}
            </Text>
          </Group>
          <ActionIcon variant="subtle" color="gray" aria-label="Edit end date" component="div">
            <IconPencil size={16} />
          </ActionIcon>
        </Group>
      </Paper>

      <DatePickerModal
        opened={showDatePicker}
        label="End Date"
        initialDate={format(addDays(new Date(), 7), 'yyyy-MM-dd')}
        onClose={() => setShowDatePicker(false)}
        onConfirm={(date) => {
          onEndDateChange(date);
          setShowDatePicker(false);
        }}
      />
    </Stack>
  );
};
